import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useState } from 'react';
import { EmptyState, PageHeader } from '@/components/page-header';
import { FileLink } from '@/components/portal/file-link';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { api } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { useOrgId } from '@/lib/org';
import type { AttachmentRow } from '@/lib/portal';

export const Route = createFileRoute('/settings/attachments')({ component: Attachments });

/** Attachments list (gear › Lists › Attachments; QBO Attachments): every attached file. */
function Attachments() {
  const orgId = useOrgId();
  const [q, setQ] = useState('');
  const list = useQuery({
    queryKey: ['portal', orgId, 'attachments', 'all'],
    queryFn: () => api<AttachmentRow[]>(`/orgs/${orgId}/attachments/all`),
    enabled: Boolean(orgId),
  });
  const needle = q.trim().toLowerCase();
  const rows = (list.data ?? []).filter(
    (a) =>
      !needle ||
      a.fileName.toLowerCase().includes(needle) ||
      a.entityLabel.toLowerCase().includes(needle),
  );
  return (
    <>
      <PageHeader
        title="Attachments"
        description="Files attached to invoices, bills, expenses, journal entries and payments. Uploads waiting to be recorded are in Documents."
      />
      {!orgId ? (
        <EmptyState title="Select a client" hint="Use the client switcher in the top bar." />
      ) : list.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : list.isError ? (
        <EmptyState title="Could not load attachments" hint={list.error.message} />
      ) : !list.data.length ? (
        <EmptyState
          title="No attachments yet"
          hint="Attach files on a saved invoice, bill, expense or journal entry, or record an upload from Documents."
        />
      ) : (
        <>
          <div className="relative mb-3 w-[320px]">
            <Search className="pointer-events-none absolute left-2 top-2.5 size-4 text-muted-foreground" />
            <Input
              className="pl-8"
              placeholder="Find a file or transaction"
              aria-label="Find a file or transaction"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="rounded-lg border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>File</TableHead>
                  <TableHead>Attached to</TableHead>
                  <TableHead className="w-[150px]">Uploaded by</TableHead>
                  <TableHead className="w-[150px]">Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((a) => (
                  <TableRow key={a.attachmentId} data-testid="attachment-row">
                    <TableCell className="max-w-[360px]">
                      <FileLink file={a} />
                    </TableCell>
                    <TableCell>
                      {a.entityLink ? (
                        <Link className="text-primary hover:underline" to={a.entityLink}>
                          {a.entityLabel}
                        </Link>
                      ) : (
                        a.entityLabel
                      )}
                    </TableCell>
                    <TableCell>{a.uploadedBy}</TableCell>
                    <TableCell className="tnum">{formatDateTime(a.createdAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </>
  );
}
